import isEmail from 'validator/lib/isEmail'
import { create, enforce, only, test } from 'vest'

import { getClosestElement, getElementChild, getElementChildren, getElements } from '../Utility/Dom'
import { IS_BAD, IS_LOADING, IS_OK } from '../Utility/State'
import { isValidJson } from '../Validation/Json'

enforce.extend({
  isEmail,
  isSingleFile: (value: { name?: string }) => {
    return value.name !== ''
  }
})

const ERROR_CODE_EMPTY = '1221560910'
const ERROR_CODE_EMAIL = '1221559976'
const ERROR_CODE_INTEGER = '1221560718'
const ERROR_CODE_COUNT = '1475002994'

interface CountRule {
  max: number
  message: string
  min: number
}

type FormData = Record<string, { name: string } | boolean | globalThis.File | string | string[]>

interface FormValidationObject {
  field: string
  rules: ValidationRules
}

interface ValidationMessage {
  message: string
}

interface ValidationRules {
  count?: CountRule
  email?: ValidationMessage
  integer?: ValidationMessage
  noFile?: ValidationMessage
  notEmpty?: ValidationMessage
}

type ValidationSuite = ReturnType<typeof create>

interface ValidationSuiteEntry {
  formId: string
  formValidationObjects: FormValidationObject[]
  formValidationSuiteInstance: ValidationSuite
}

export default class FormValidationService {
  forms: HTMLFormElement[]
  registeredElements: Set<HTMLInputElement>
  validationSuites: ValidationSuiteEntry[]

  constructor () {
    this.forms = Array.from(getElements('form')) as HTMLFormElement[]
    this.registeredElements = new Set()
    this.validationSuites = []
  }

  addError (container: HTMLElement, formElements: NodeListOf<HTMLElement>, errors: string[]): void {
    const errorContainer = getElementChild(container, 'form-error')
    if (errorContainer === null) return

    errorContainer.innerText = ''
    errors.forEach((error) => {
      errorContainer.innerText += error + '\n'
    })

    formElements.forEach((formElement) => formElement.setAttribute('aria-invalid', 'true'))
  }

  attachFieldListeners (form: HTMLFormElement, formId: string, formElement: HTMLInputElement): void {
    const formElementType = formElement.type

    if (
      formElementType === 'radio' ||
      formElementType === 'checkbox' ||
      formElementType === 'select-one'
    ) {
      formElement.addEventListener('change', () => {
        this.handleFormElementInput(form, formId, formElement)
      })
    } else {
      formElement.addEventListener('input', () => {
        this.removeErrorsForFormElement(form, formElement.name)
      })

      // Validate whenever the user leaves the field, even if they typed nothing:
      // focusing an empty required field and clicking away must surface the error.
      formElement.addEventListener('blur', () => {
        this.handleFormElementInput(form, formId, formElement)
      })
    }
  }

  createValidationSuite (formValidationObjects: FormValidationObject[]): ValidationSuite {
    return create((data: FormData = {}, currentField?: string) => {
      only(currentField)

      formValidationObjects.forEach((formValidationObject) => {
        const fieldElement = document.querySelector<HTMLInputElement>(`[name="${formValidationObject.field}"]`)
        if (!fieldElement || fieldElement.disabled) {
          return
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'notEmpty')) {
          test(formValidationObject.field, formValidationObject.rules.notEmpty!.message, () => {
            const value = data[formValidationObject.field]
            if (typeof value === 'boolean') {
              enforce(value).isTruthy()
            } else if (Array.isArray(value)) {
              enforce(value.length).greaterThan(0)
            } else {
              enforce(value).isNotBlank()
            }
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'count')) {
          const countRule = formValidationObject.rules.count!
          test(formValidationObject.field, countRule.message, () => {
            const value = data[formValidationObject.field]
            const count = Array.isArray(value) ? value.length : (value ? 1 : 0)
            enforce(count)
              .greaterThanOrEquals(countRule.min ?? 0)
              .lessThanOrEquals(countRule.max ?? Number.MAX_SAFE_INTEGER)
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'noFile')) {
          const submittedFile = document.querySelector('[name="' + formValidationObject.field + '[submittedFile][resourcePointer]"]')

          if (submittedFile !== null) {
            return
          }

          test(formValidationObject.field, formValidationObject.rules.noFile!.message, () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- isSingleFile is a runtime-extended enforce rule (see enforce.extend above)
            const enforced = enforce(data[formValidationObject.field]) as any
            enforced.isSingleFile()
          })
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'email')) {
          if (data[formValidationObject.field] && data[formValidationObject.field] !== '') {
            test(formValidationObject.field, formValidationObject.rules.email!.message, () => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- isEmail is a runtime-extended enforce rule (see enforce.extend above)
              const enforced = enforce(data[formValidationObject.field]) as any
              enforced.isEmail()
            })
          }
        }

        if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'integer')) {
          if (data[formValidationObject.field] && data[formValidationObject.field] !== '') {
            test(formValidationObject.field, formValidationObject.rules.integer!.message, () => {
              enforce(data[formValidationObject.field]).matches(/^\d+$/)
            })
          }
        }
      })
    })
  }

  getFormData (form: HTMLFormElement): FormData {
    const formElements = Array.from(getElementChildren(form, 'form-element')) as HTMLInputElement[]
    const data: FormData = {}

    formElements.forEach((element) => {
      if (!element.disabled && element.name) {
        if (element.type === 'checkbox') {
          if (element.name.endsWith('[]')) {
            const values = Array.isArray(data[element.name]) ? data[element.name] as string[] : []
            if (element.checked) {
              values.push(element.value)
            }
            data[element.name] = values
          } else {
            data[element.name] = element.checked
          }
        } else if (element.type === 'radio') {
          if (element.checked) {
            data[element.name] = element.value
          }
        } else if (element.type === 'file') {
          data[element.name] = (element.files && element.files[0]) || { name: '' }
        } else {
          data[element.name] = element.value
        }
      }
    })

    return data
  }

  getFormDataAndRemoveFormErrors (form: HTMLFormElement): FormData {
    const formElements = Array.from(getElementChildren(form, 'form-element')) as HTMLInputElement[]

    formElements.forEach((formElement) => {
      this.removeErrorsForFormElement(form, formElement.name)
    })

    return this.getFormData(form)
  }

  getFormValidationSuiteByFormId (formIdentifier: string): undefined | ValidationSuiteEntry {
    return this.validationSuites.find((validationSuite) => {
      return validationSuite.formId === formIdentifier
    })
  }

  handleFormElementInput (form: HTMLFormElement, formId: string, formElement: HTMLInputElement): void {
    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)
    if (typeof formValidationSuiteObject === 'undefined') return

    const formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance
    this.removeErrorsForFormElement(form, formElement.name)
    const fieldValidationObject = this.getFormData(form)
    formValidationSuite.reset()

    const result = formValidationSuite.run(
      fieldValidationObject,
      formElement.name
    )

    if (result.hasErrors()) {
      this.showErrors(form, result.getErrors())
    } else {
      this.markFormElementValid(form, formElement.name)
    }
  }

  handleFormReset (form: HTMLFormElement, formId: string): void {
    // The browser resets the field values natively; clear the JS-managed validation
    // states (is-bad/is-ok classes, error/success messages) and the vest suite so the
    // form returns to its pristine appearance instead of keeping stale hints.
    const formElements = Array.from(getElementChildren(form, 'form-element')) as HTMLInputElement[]
    formElements.forEach((formElement) => {
      this.removeErrorsForFormElement(form, formElement.name)
    })

    this.getFormValidationSuiteByFormId(formId)?.formValidationSuiteInstance.reset()
  }

  handleFormSubmit (form: HTMLFormElement, formId: string, event: SubmitEvent): void {
    // Validate on forward navigation (multi-step "next" buttons) as well as the final submit, so
    // each step is checked client-side before advancing. Skip backward navigation (formnovalidate)
    // and programmatic submits that carry no submitter.
    if (!event.submitter || event.submitter.hasAttribute('formnovalidate')) {
      return
    }

    const submitButton = getElementChild(form, 'form-submit') as HTMLButtonElement | null

    // Prevent double submissions
    if (submitButton?.disabled) {
      event.preventDefault()
      return
    }

    const formElements = Array.from(getElementChildren(form, 'form-element')) as HTMLInputElement[]
    const activeFieldNames = Array.from(formElements)
      .filter((el) => !el.disabled && el.name)
      .map((el) => el.name)

    formElements.forEach((formElement) => {
      if (formElement.disabled) return

      this.handleFormElementInput(form, formId, formElement)
    })

    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)

    if (typeof formValidationSuiteObject === 'undefined') {
      return
    }

    const formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance.run(
      this.getFormDataAndRemoveFormErrors(form)
    )

    const errors = Object.fromEntries(
      Object.entries(formValidationSuite.getErrors())
        .filter(([field]) => activeFieldNames.includes(field))
    )

    const errorFieldNames = Object.keys(errors)

    if (errorFieldNames.length > 0) {
      event.preventDefault()
      this.showErrors(form, errors, true)

      activeFieldNames
        .filter((fieldName) => !errorFieldNames.includes(fieldName))
        .forEach((fieldName) => this.markFormElementValid(form, fieldName))
    } else {
      activeFieldNames.forEach((fieldName) => {
        this.removeErrorsForFormElement(form, fieldName)
        this.markFormElementValid(form, fieldName)
      })

      // Disable submit button AFTER form submission starts (with minimal delay)
      // to prevent double clicks while still sending the button value
      if (submitButton) {
        submitButton.classList.add(IS_LOADING)
        setTimeout(() => {
          submitButton.disabled = true
        }, 10)
      }
    }
  }

  init (): void {
    this.initScrollToSection()
    this.initScrollToSectionButtons()

    this.forms.forEach((form) => {
      const formId = form.getAttribute('data-id')
      if (!formId) return

      const formElements = Array.from(getElementChildren(form, 'form-element')) as HTMLInputElement[]
      const formValidationObjects: FormValidationObject[] = []

      formElements.forEach((formElement) => {
        this.registerFormElement(form, formId, formElement, formValidationObjects)
      })

      form.setAttribute('novalidate', 'novalidate')
      this.initFormValidationSuite(formId, formValidationObjects)

      form.addEventListener('submit', (event) => {
        this.handleFormSubmit(form, formId, event as SubmitEvent)
      })

      form.addEventListener('reset', () => {
        this.handleFormReset(form, formId)
      })
    })
  }

  initFormValidationSuite (formIdentifier: string, formValidationObjects: FormValidationObject[]): void {
    this.validationSuites.push({
      formId: formIdentifier,
      formValidationObjects: formValidationObjects,
      formValidationSuiteInstance: this.createValidationSuite(formValidationObjects)
    })
  }

  initScrollToSection (): void {
    const scrollTarget = window.sessionStorage.getItem('formScrollToSection')
    if (!scrollTarget) return

    window.sessionStorage.removeItem('formScrollToSection')

    const targetElement =
      document.querySelector<HTMLElement>(`[data-identifier="${scrollTarget}"]`) ||
      document.getElementById(scrollTarget)

    if (targetElement) {
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }

  initScrollToSectionButtons (): void {
    const scrollToButtons = document.querySelectorAll<HTMLElement>('[data-scroll-to]')

    scrollToButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const scrollTarget = button.dataset.scrollTo
        if (scrollTarget) {
          window.sessionStorage.setItem('formScrollToSection', scrollTarget)
        }
      })
    })
  }

  markFormElementValid (form: HTMLFormElement, formElementName: string): void {
    const formElements = form.querySelectorAll<HTMLElement>('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    const container = getClosestElement(formElements[0], 'form-ctr')
    if (!container) return

    container.classList.add(IS_OK)
  }

  registerFormElement (form: HTMLFormElement, formId: string, formElement: HTMLInputElement, formValidationObjects: FormValidationObject[]): void {
    if (this.registeredElements.has(formElement)) {
      return
    }

    this.registeredElements.add(formElement)

    const formGroup = getClosestElement(formElement, 'form-group')
    const formElementType = formElement.type
    const validationRules: ValidationRules = {}

    if (formGroup && isValidJson(formGroup.dataset.msg ?? '') === true) {
      const errorMessages: Array<{ code: number | string, message: string }> = JSON.parse(formGroup.dataset.msg!)
      const emptyError = errorMessages.filter((errorMessage) => String(errorMessage.code) === ERROR_CODE_EMPTY)[0]
      const emailError = errorMessages.filter((errorMessage) => String(errorMessage.code) === ERROR_CODE_EMAIL)[0]
      const integerError = errorMessages.filter((errorMessage) => String(errorMessage.code) === ERROR_CODE_INTEGER)[0]
      const countError = errorMessages.filter((errorMessage) => String(errorMessage.code) === ERROR_CODE_COUNT)[0]

      if (emptyError && formElementType !== 'file') {
        validationRules.notEmpty = {
          message: emptyError.message
        }
      }

      if (emptyError && formElementType === 'file') {
        validationRules.noFile = {
          message: emptyError.message
        }
      }

      if (emailError) {
        validationRules.email = {
          message: emailError.message
        }
      }

      if (integerError) {
        validationRules.integer = {
          message: integerError.message
        }
      }

      const minCount = formGroup.dataset.minCount
      const maxCount = formGroup.dataset.maxCount

      if (minCount || maxCount) {
        validationRules.count = {
          max: maxCount ? parseInt(maxCount, 10) : Number.MAX_SAFE_INTEGER,
          message: countError ? countError.message : '',
          min: minCount ? parseInt(minCount, 10) : 0
        }
      }

      const noValidationMessagesProvided = Object.keys(validationRules).length === 0 &&
        (formElement.required === true || formElement.getAttribute('data-required') === 'true')

      if (noValidationMessagesProvided === true) {
        if (formElementType !== 'file') {
          validationRules.notEmpty = { message: '' }
        } else {
          validationRules.noFile = { message: '' }
        }
      }
    }

    const alreadyRegistered = formValidationObjects.some((object) => object.field === formElement.name)
    if (alreadyRegistered === false) {
      formValidationObjects.push({
        field: formElement.name,
        rules: validationRules
      })
    }

    this.attachFieldListeners(form, formId, formElement)
  }

  removeErrorsForFormElement (form: HTMLFormElement, formElementName: string): void {
    const formElements = form.querySelectorAll<HTMLElement>('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    formElements.forEach((formElement) => formElement.removeAttribute('aria-invalid'))

    const container = getClosestElement(formElements[0], 'form-ctr')
    if (!container) return

    container.classList.remove(IS_BAD, IS_OK)

    const errorContainer = getElementChild(container, 'form-error')
    if (!errorContainer) return
    errorContainer.innerText = ''

    const successContainer = getElementChild(container, 'form-success')
    if (successContainer) {
      successContainer.innerText = ''
    }
  }

  showErrors (form: HTMLFormElement, errorsByFormField: Record<string, string[]>, focusFirstError = false): void {
    for (const [formElementName, errorMessages] of Object.entries(errorsByFormField)) {
      this.showErrorsForFormElement(form, formElementName, errorMessages)
    }

    // Only move focus on submit. Doing it during per-field blur validation would
    // re-focus the field the user is trying to leave, creating a keyboard trap.
    if (focusFirstError === false) return

    const firstErrorElementName = Object.entries(errorsByFormField)[0][0]
    const firstErrorElement = form.querySelector<HTMLElement>('[data-js="form-element"][name="' + firstErrorElementName + '"]')

    if (firstErrorElement !== null) {
      let elementToScrollTo: HTMLElement | null = firstErrorElement

      if (window.getComputedStyle(elementToScrollTo).display === 'none') {
        elementToScrollTo = elementToScrollTo.parentElement
      }

      elementToScrollTo?.scrollIntoView()
      elementToScrollTo?.focus({
        preventScroll: true
      })
    }
  }

  showErrorsForFormElement (form: HTMLFormElement, formElementName: string, errorMessages: string[]): void {
    const formElements = form.querySelectorAll<HTMLElement>('[data-js="form-element"][name="' + formElementName + '"]')
    if (formElements.length === 0) return

    const container = getClosestElement(formElements[0], 'form-ctr')
    if (!container) return

    container.classList.remove(IS_OK)
    container.classList.add(IS_BAD)

    this.addError(container, formElements, errorMessages)
  }
}
